import React, { ReactNode, useState } from 'react';
import { Box, CircularProgress, IconButton, Drawer, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Menu as MenuIcon } from '@mui/icons-material';
import NavigationRail from './NavigationRail';
import MobileNavigation from './MobileNavigation';
import EnhancedAiraAI from '../ai/EnhancedAiraAI';
import { useAuth } from '../../contexts/AuthContext';
import { useBranding } from '../../contexts/ThemeContext';
import apiClient from '../../services/api/config';

const RAIL_WIDTH = 80;

const LayoutContainer = styled(Box)({
  display: 'flex',
  minHeight: '100vh',
  backgroundColor: '#F3F6F9',
  overflowX: 'hidden',
  maxWidth: '100vw',
});

const MainContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  marginLeft: RAIL_WIDTH,
  minHeight: '100vh',
  padding: '24px',
  backgroundColor: '#F3F6F9',
  maxWidth: '100%',
  overflowX: 'hidden' as const,
  boxSizing: 'border-box' as const,
  [theme.breakpoints.down('md')]: {
    marginLeft: 0,
    padding: '12px',
    paddingTop: '68px',
    maxWidth: '100vw',
  },
}));


interface MainLayoutProps {
  children: ReactNode;
}

function MainLayout({ children }: MainLayoutProps) {
  const { user, isLoading } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { branding } = useBranding();

  const sidebarBg = branding.sidebarColor || '#1A1D1F';

  const resolveUrl = (url: string) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/r2/')) return `${apiClient.defaults.baseURL || ''}${url}`;
    return url;
  };

  if (isLoading || !user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <LayoutContainer>
      {isMobile ? (
        <>
          <Box sx={{
            display: { xs: 'flex', md: 'none' },
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: '56px',
            backgroundColor: sidebarBg,
            alignItems: 'center',
            padding: '0 16px',
            zIndex: 1100,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}>
            <IconButton 
              onClick={() => setMobileMenuOpen(true)}
              sx={{ color: branding.sidebarTextColor || 'white', mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            {branding.logoUrl ? (
              <Box
                component="img"
                src={resolveUrl(branding.logoUrl)}
                alt={branding.companyName || 'Logo'}
                sx={{ width: 28, height: 28, objectFit: 'contain' }}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = 'none'; }}
              />
            ) : (
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/brand/logo-mark.svg`}
                alt="MoreMe AI"
                sx={{ width: 28, height: 28, filter: 'brightness(10)' }}
              />
            )}
          </Box>
          <Drawer
            anchor="left"
            open={mobileMenuOpen}
            onClose={() => setMobileMenuOpen(false)}
            PaperProps={{ sx: { backgroundColor: 'transparent' } }}
          >
            <MobileNavigation onClose={() => setMobileMenuOpen(false)} />
          </Drawer>
        </>
      ) : (
        <NavigationRail />
      )}
      <MainContent>
        {children}
      </MainContent>
      <EnhancedAiraAI />
    </LayoutContainer>
  );
}

export default MainLayout;
